import React from "react";

// Material/Google-style indeterminate spinner. Inherits color from its
// parent so it sits cleanly inside any button/link/menu without extra
// configuration. CSS lives in styles.css under `.material-spinner`.
export default function Spinner({
  size = 14,
  className = "",
  label = "Loading",
}: { size?: number; className?: string; label?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 50 50"
      className={"material-spinner shrink-0 " + className}
      role={label ? "status" : undefined}
      aria-label={label}
    >
      <circle cx="25" cy="25" r="20" />
    </svg>
  );
}

// "Loading X…" inline label with the Material spinner. Use for transient
// per-pane loading states (request detail, invite lookup, etc).
export function InlineLoading({ label = "Loading…", size = 14, className = "" }: { label?: string; size?: number; className?: string }) {
  return (
    <div className={"inline-flex items-center gap-2 muted text-sm " + className}>
      <Spinner size={size} label={label} />
      <span>{label}</span>
    </div>
  );
}

export function LoadingButton({
  pending,
  className = "btn",
  children,
  pendingLabel,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  pending?: boolean;
  pendingLabel?: string;
}) {
  return (
    <button
      {...rest}
      className={className}
      disabled={rest.disabled || pending}
      aria-busy={pending || undefined}
    >
      {pending && <Spinner />}
      <span>{pending && pendingLabel ? pendingLabel : children}</span>
    </button>
  );
}

export function LoadingLinkButton({
  href,
  className = "btn-primary",
  children,
  pendingLabel = "Opening…",
  startIcon,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  pendingLabel?: string;
  startIcon?: React.ReactNode;
}) {
  const [navigating, setNavigating] = React.useState(false);
  // If the user clicks through (e.g. to the Google OAuth page) and then
  // presses Back, the browser restores this page from the back/forward
  // cache with the old React state — spinner still going. `pageshow`
  // with `persisted` fires exactly on that restore, so reset there.
  React.useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setNavigating(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  return (
    <button
      type="button"
      className={className}
      disabled={navigating}
      aria-busy={navigating || undefined}
      onClick={() => {
        if (navigating) return;
        setNavigating(true);
        window.location.href = href;
      }}
    >
      {navigating ? <Spinner /> : startIcon}
      <span>{navigating ? pendingLabel : children}</span>
    </button>
  );
}
